import { useEffect, useState } from "react";
import useTitleStore from "../../store/titleStore";
import useDebounce from "../../hooks/useDebounce";
import { useTableHeight } from "../../hooks/useTableHeight";
import { useNavigate } from "react-router";
import { Button, Card, Flex, Form, Grid, Input, message, Pagination, Select, Table, Tag } from "antd";
import { Icon } from "@iconify/react/dist/iconify.js";
import { registrationService } from "../../services/registration.service";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { format } from 'date-fns';
import { indonesiaRegionService } from "../../services/indonesia-region.service";
import RenderIf from "../../components/RenderIf";
import RegistrationModalFilter from "../../components/RegistrationModalFilter";

export default function PendingRegistration(){

  const breakpoint = Grid.useBreakpoint()

  const navigate = useNavigate();

  const { setTitle } = useTitleStore();

  const tableHeight = useTableHeight();

  const [,contextHolder] = message.useMessage()

  const [keyword, setKeyword] = useState("");
  const [selectedCity, setSelectedCity] = useState('')
  const [selectedDistrict, setSelectedDistrict] = useState('')
  const [responsiveFilter, setResponsiveFilter] = useState(false)
  const debouncedKeyword = useDebounce(keyword, 500);

  const [filterForm] = Form.useForm()

  const city = Form.useWatch('city', filterForm)
  const district = Form.useWatch('district', filterForm)

  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    filter: [],
    keyword: null,
    city: null,
    district: null,
    village: null,
    source: null
  })

  useEffect(() => {
    setTitle("SR Masuk");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams({
      ...dataParams,
      keyword: debouncedKeyword
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKeyword]);


  const { data: registrationData , isLoading, isRefetching } = useQuery({
    queryKey: ['registration', dataParams],
    queryFn: () => registrationService.getNeedConfirmationData(dataParams),
    placeholderData: keepPreviousData,
  })

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page
    })
  }

  const { data: cities } = useQuery({
    queryKey: ['cities'],
    queryFn: () => indonesiaRegionService.getCities()
  })

  const { data: districts } = useQuery({
    queryKey: ['districts', selectedCity],
    queryFn: () => indonesiaRegionService.getDistricts(selectedCity),
    enabled: !!selectedCity
  })

  const { data: villages } = useQuery({
    queryKey: ['districts', selectedDistrict],
    queryFn: () => indonesiaRegionService.getSubdistricts(selectedDistrict),
    enabled: !!selectedDistrict
  })

  useEffect(() => {

    filterForm.resetFields(['district', 'village'])

    if(city){
      const selected = cities.find(e => e.name == city)
      setSelectedCity(selected.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city])

  useEffect(() => {

    filterForm.resetFields(['village'])
    
    if(district){
      const selected = districts.find(e => e.name == district)
      setSelectedDistrict(selected.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [district])


  const columns = [
    {
      title: 'No',
      align: 'center',
      width: 80,
      fixed: breakpoint.md && 'left',
      render: (text, record, index) =>  index + 1,
    },
    {
      title: 'Tracking ID',
      dataIndex: 'tracking_id',
      width: 120,
      fixed: breakpoint.md && 'left',
    },
    {
      title: 'Nama Calon Pelanggan',
      dataIndex: 'nama_kustomer',
      width: 180
    },
    {
      title: 'No Telp',
      dataIndex: 'nomor_kustomer',
      width: 150
    },
    {
      title: 'Email',
      dataIndex: 'email_kustomer',
      width: 230
    },
    { 
      title: 'Kota',
      dataIndex: 'kota_kustomer',
      width: 180,
    },
    { 
      title: 'Kelurahan',
      dataIndex: 'kelurahan_kustomer',
      width: 150,
    },
    { 
      title: 'RT/RW',
      width: 120,
      render: val => `${val.rt_kustomer?.toString().padStart(3, '0')}/${val.rw_kustomer?.toString().padStart(3, '0')}`
    },
    { 
      title: 'AB',
      width: 180,
      render: val => val?.ab?.NAMA_AB
    },
    {
      title: 'Tanggal Pendaftaran',
      dataIndex: 'createdAt',
      width: 150,
      // fixed: 'left',
      render: val => val && format(new Date(val), 'dd/MM/yyyy - HH.mm')
    },
    {
      title: 'Sumber Pendaftaran',
      dataIndex: 'source',
      width: 150,
      // fixed: 'left',
      render: val => (
        <Tag>
          {['surveyor', 'website'].includes(val) ? 'Website' : 'Super App'}
        </Tag>
      )
    },
    {
      className: 'last-cell-p',
      title: 'Aksi',
      align: 'right',
      width: 80,
      fixed: 'right',
      render: (record) => (
        <Button  
          icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
          color="primary"
          variant="filled"
          onClick={() => navigate('/registration/detail/'+record.id)} 
          size=""
        />
      )
    },
  ]

  return (
    <>
      {contextHolder}
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0
          }
        }}
      >
        <RenderIf when={breakpoint.lg} >
          <Form form={filterForm} >
            <Flex justify="flex-start" gap={20} style={{ paddingInline: 24, marginBottom: 24 }} >
              <Input 
                style={{ width: 250 }} 
                prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
                placeholder="cari.."
                onChange={e => setKeyword(e.target.value)}
                value={keyword}
              />
              <Form.Item noStyle name={'city'} >
                <Select
                  allowClear
                  options={cities?.map(e => ({ label: e.name, value: e.name }))}
                  style={{ width: 200 }}
                  placeholder="Kota"
                  onChange={(e) => setDataParams({ ...dataParams, city: e })}
                />
              </Form.Item>
              <Form.Item noStyle name={'district'} >
                <Select
                  allowClear
                  options={districts?.map(e => ({ label: e.name, value: e.name }))}
                  style={{ width: 200 }}
                  placeholder="Kecamatan"
                  disabled={!city}
                  onChange={(e) => setDataParams({ ...dataParams, district: e })}
                />
              </Form.Item>
              <Form.Item name={'village'} noStyle >
                <Select
                  allowClear
                  options={villages?.map(e => ({ label: e.name, value: e.name }))}
                  style={{ width: 200 }}
                  placeholder="Kelurahan"
                  disabled={!district}
                  onChange={(e) => setDataParams({ ...dataParams, village: e })}
                />
              </Form.Item>
              <Select
                style={{ width: 180 }}
                placeholder="Sumber"
                onChange={(e) => setDataParams({ ...dataParams, source: e })}
                allowClear
                options={[
                  {
                    label: 'Super App',
                    value: 'SUPER_APP'
                  },
                  {
                    label: 'Website',
                    value: 'WEBSITE'
                  }
                ]}
              />
            </Flex>
          </Form>
        </RenderIf>
        <RenderIf when={!breakpoint.lg} >
          <Flex justify="flex-end" gap={20} style={{ padding: '0 24px', marginBottom: 24 }} >
            <Button 
              variant="outlined" 
              color="primary" 
              icon={<Icon icon="mynaui:filter-solid" width="22" height="22" />}
              onClick={() => setResponsiveFilter(true)}
            >
              Filter
            </Button>
          </Flex>
        </RenderIf>
        <Table
          id="registration-table"
          columns={columns}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={'id'}
          scroll={{ y: tableHeight }}
          dataSource={registrationData?.data}
          pagination={false}
          rowClassName={(record) => !record.is_open ? 'row-open' : ''}
        />
        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={registrationData?.pagination?.total}
          showSizeChanger
          pageSizeOptions={['10', '20', '50']}
          current={registrationData?.pagination?.page}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>
      <RegistrationModalFilter
        form={filterForm}
        setKeyword={setKeyword}
        keyword={keyword}
        city={city}
        cities={cities}
        responsiveFilter={responsiveFilter}
        setResponsiveFilter={setResponsiveFilter}
        district={district}
        districts={districts}
        villages={villages}
      />
    </>
  )
}